/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import {
	useStarterDashboardV2PageData,
	useStarterDashboardV2PageFcm,
} from 'src/app/pages/starterDashboardV2/pageContext';
import {
	COMPLETE_TASK_ERROR_MAP,
	useCompleteTask,
	useTriggerDownloadFlyerModal,
	useTriggerSharePetitionModal,
	useTriggerVanityLinkModal,
} from 'src/app/pages/starterDashboardV2/shared/hooks';

import type { Task } from '../../../../types';

export function useGetAction() {
	const track = useTracking();
	const fcms = useStarterDashboardV2PageFcm();
	const showMultiDownloadFlyersTask = fcms.multipleQrCodeFlyersEnabled;

	const {
		actions: { refreshData },
	} = useStarterDashboardV2PageData();

	const triggerSharePetitionModal = useTriggerSharePetitionModal();
	const triggerVanityLinkModal = useTriggerVanityLinkModal();
	const triggerDownloadFlyerModal = useTriggerDownloadFlyerModal('starter_dashboard_task');

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { mutate: completeTask } = useCompleteTask({
		onError: (error) => {
			if (error === COMPLETE_TASK_ERROR_MAP.UNAUTHORIZED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join/redirected?redirectTo=${params}`);
			}
		},
	});

	return useCallback(
		({ id, name }: { id: Task['id']; name: Task['name'] }) => {
			switch (name) {
				case 'customize_link': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name });
						await triggerVanityLinkModal();
						// DO NOT COMPLETE - task is completed asynchronously in backend service
						refreshData();
						return Promise.resolve(true);
					};
				}
				case 'download_flyer': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name });
						if (showMultiDownloadFlyersTask) {
							await triggerDownloadFlyerModal();
						}

						await completeTask(id);
						refreshData();
						return Promise.resolve(true);
					};
				}
				case 'promote_basic':
				case 'promote_high_performer':
				case 'share_supporters': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name });
						await completeTask(id);
						refreshData();
						return Promise.resolve(true);
					};
				}
				case 'share_communities':
				case 'share_friends':
				case 'share_social':
				case 'share_top_starter_share_channel': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name });
						await triggerSharePetitionModal();
						await completeTask(id);
						refreshData();
						return Promise.resolve(true);
					};
				}
				default:
					return async () => {
						return Promise.resolve(true);
					};
			}
		},
		[
			completeTask,
			refreshData,
			showMultiDownloadFlyersTask,
			track,
			triggerDownloadFlyerModal,
			triggerSharePetitionModal,
			triggerVanityLinkModal,
		],
	);
}
